import type { usePostHog } from '@posthog/next'

import { POSTHOG_AUTH_EVENTS, SEARCH_PARAM_KEYS } from '@/constants/auth'
import { hashAccountId } from '@/lib/feed-analytics'

type PostHogClient = Pick<ReturnType<typeof usePostHog>, 'identify' | 'capture'>

/**
 * Whether Reticulum created the account on this OAuth callback (CONNECT-6210).
 * A missing flag counts as a returning account, so an older Reticulum never inflates sign-ups.
 */
export const isNewAccountFromCallback = (searchParams: Pick<URLSearchParams, 'get'>) =>
  searchParams.get(SEARCH_PARAM_KEYS.IS_NEW_USER) === 'true'

/**
 * Identifies the user by the SHA-256 hash of their HTC account ID and sends `register` or `login`
 * (CONNECT-6190). The raw account ID never reaches PostHog; without one, nothing is sent.
 *
 * Sent instantly because a new account may be redirected to profile setup right after this.
 * Never throws: analytics must not block the redirect and URL cleanup that follow it.
 */
export const trackPostHogAuthEvent = async (
  posthog: PostHogClient,
  {
    accountId,
    isNewAccount,
    shouldIdentify,
  }: {
    accountId: string
    isNewAccount: boolean
    /** False in the app webview, which shares the native app's PostHog distinct ID. */
    shouldIdentify: boolean
  },
) => {
  const hashaccountID = await hashAccountId(accountId)
  if (!hashaccountID) return

  try {
    if (shouldIdentify) posthog.identify(hashaccountID)
    posthog.capture(
      isNewAccount ? POSTHOG_AUTH_EVENTS.REGISTER : POSTHOG_AUTH_EVENTS.LOGIN,
      { hashaccountID },
      { send_instantly: true },
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[Auth Analytics] Failed to send PostHog auth event:', error)
  }
}
